import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { getProgressIndicatorStyle } from '@/lib/progress-utils';
import { cn } from '@/lib/utils';
import { X, Plus } from 'lucide-react';
import { CronogramaAtividadeSubtarefa } from '@/types';
import { cronogramaSubtarefaService } from '@/services/cronogramasApi';
import { toast } from 'sonner';

interface SubtarefasSectionProps {
  cronogramaId: string;
  atividadeId: string;
  subtarefas: CronogramaAtividadeSubtarefa[];
  onSubtarefasChange: (subtarefas: CronogramaAtividadeSubtarefa[]) => void;
}

export function SubtarefasSection({ cronogramaId, atividadeId, subtarefas, onSubtarefasChange }: SubtarefasSectionProps) {
  const [novoTitulo, setNovoTitulo] = useState('');
  const [adicionando, setAdicionando] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const total = subtarefas.length;
  const concluidas = subtarefas.filter(s => s.concluida).length;
  const percentual = total > 0 ? Math.round((concluidas / total) * 100) : 0;

  const handleAdd = async () => {
    const titulo = novoTitulo.trim();
    if (!titulo) return;
    setAdicionando(true);
    const res = await cronogramaSubtarefaService.create(cronogramaId, atividadeId, titulo);
    setAdicionando(false);
    if (res.success) {
      setNovoTitulo('');
      onSubtarefasChange([...subtarefas, res.data]);
    } else {
      toast.error(res.error || 'Erro ao adicionar subatividade');
    }
  };

  const handleToggle = async (subtarefa: CronogramaAtividadeSubtarefa) => {
    setBusyId(subtarefa.id);
    const res = await cronogramaSubtarefaService.update(cronogramaId, atividadeId, subtarefa.id, { concluida: !subtarefa.concluida });
    setBusyId(null);
    if (res.success) {
      onSubtarefasChange(subtarefas.map(s => s.id === subtarefa.id ? res.data : s));
    } else {
      toast.error(res.error || 'Erro ao atualizar subatividade');
    }
  };

  const handleDelete = async (subtarefa: CronogramaAtividadeSubtarefa) => {
    setBusyId(subtarefa.id);
    const res = await cronogramaSubtarefaService.delete(cronogramaId, atividadeId, subtarefa.id);
    setBusyId(null);
    if (res.success) {
      onSubtarefasChange(subtarefas.filter(s => s.id !== subtarefa.id));
    } else {
      toast.error(res.error || 'Erro ao remover subatividade');
    }
  };

  return (
    <div className="space-y-2">
      {total > 0 && (
        <Progress value={percentual} className={cn("h-1.5", getProgressIndicatorStyle(percentual))} />
      )}
      <div className="space-y-1">
        {subtarefas.map(s => (
          <div key={s.id} className="flex items-center gap-2">
            <Checkbox
              checked={s.concluida}
              disabled={busyId === s.id}
              onCheckedChange={() => handleToggle(s)}
            />
            <span className={`flex-1 text-sm ${s.concluida ? 'line-through text-muted-foreground' : ''}`}>
              {s.titulo}
            </span>
            <Button
              variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-red-600"
              disabled={busyId === s.id}
              onClick={() => handleDelete(s)}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-1 rounded-md border border-input bg-background pl-3 pr-1 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background">
        <input
          placeholder="Nova subatividade..."
          value={novoTitulo}
          onChange={e => setNovoTitulo(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
          className="h-8 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" disabled={adicionando || !novoTitulo.trim()} onClick={handleAdd}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
